import axios from "../../utils/axios";

import { useEffect, useState } from "react";
import "./css/card-editor.css";
import Modal from "../Modal/Modal";

const CardEditor = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [cardImage, setCardImage] = useState(null);
  const [cardStyles, setCardStyles] = useState([]);
  const [isModalOpened, setIsModalOpened] = useState(false);

  const fetchImages = async () => {
    try {
      const response = await axios.get("/images/gallery");
      setImages(response.data.images);
      setCardStyles(response.data.cardStyles);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleFileChange = (e) => {
    console.log("FILE CHANGE");
    setSelectedFile(e.target.files[0]);
  };

  const handleCloseModal = () => {
    setIsModalOpened(false);
  };

  const handleUpladImageClick = () => {
    setIsModalOpened(true);
  };

  const handleUploadStyle = async (e) => {
    e.preventDefault();

    const data = {
      url: cardImage,
      repeat: backgroundRepeat,
      backgroundSize: backgroundSize,
      backgroundPosition: [backgroundPositionX, backgroundPositionY],
      backgroundAnimation: backgroundAnimation 
    };
    
    setUploading(true);
    setError("");
    
    try {
      const response = await axios.post("/images/upload-style", data);
      console.log(response.data);
      setCardStyles(response.data.cardStyles);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStyle = async (styleId, e) => {
    e.preventDefault();
    try {
      const response = await axios.delete(`/images/card-styles/${styleId}`);
      setCardStyles(response.data.cardStyles);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await axios.delete(`/images/gallery/${imageId}`);
      setImages(response.data.images);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    
    const formData = new FormData();
    formData.append("image", selectedFile);
    
    setUploading(true);
    setError("");
    
    try {
      const response = await axios.post("/images/upload", formData);
      // Append the new image to the existing images list
      setImages((prevImages) => [...prevImages, response.data.image]);
      setSelectedFile(null);
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  //Style Settings
  const [backgroundRepeat, setBackgroundRepeat] = useState("repeat");
  const [backgroundSize, setBackgroundSize] = useState("100");
  const [backgroundPositionX, setBackgroundPositionX] = useState("100");
  const [backgroundPositionY, setBackgroundPositionY] = useState("100");
  const [backgroundAnimation, setBackgroundAnimation] = useState(null);

  return (
    <div className="card-editor-wrapper">
      <h4>Add or select an image</h4>
      {error}
      <div className="images-gallery">
        {images.length === 0 ? (<>
          <p>No images uploaded yet.</p>
          <button className="add-new-image" onClick={handleUpladImageClick}>+</button>
        </>
        ) : (
          <div className="images-grid">
            {images.map((img, index) => (
              <div key={index} onClick={() => setCardImage(img.url)} className={`image-item ${(cardImage === img.url) && "active"}`} >
                <div onClick={(e) => handleDeleteImage(img._id, e)} className="delete-card-style">x</div>
                <img 
                  src={img.url} 
                  alt={`Uploaded ${index}`} 
                />
              </div>
            ))}
            <button className="add-new-image" onClick={handleUpladImageClick}>+</button>
          </div>
        )}
      </div>
      <div className="card-editor-container">
        <div>
          <br />
          <h4>Apply card style</h4>
          <div className="card-editor-top">
            <div className="card-editor" style={{
              backgroundImage: `url(${cardImage})`,
              backgroundRepeat,
              backgroundSize: backgroundSize + "%",
              backgroundPositionX: backgroundPositionX + "%",
              backgroundPositionY: backgroundPositionY + "%",
              animation: backgroundAnimation
            }} />
            <div className="card-editor-style-config">
              <fieldset>
                <legend>Background repeat:</legend>
                <div>
                  <input 
                    type="radio" 
                    id="no-repeat" 
                    name="background-repeat" 
                    value="no-repeat" 
                    checked={backgroundRepeat === "no-repeat"}
                    onChange={(e) => setBackgroundRepeat(e.target.value)} />
                  <label htmlFor="no-repeat">No repeat</label>
                </div>
                <div>
                  <input 
                    type="radio"
                    id="repeat"
                    name="background-repeat"
                    value="repeat"
                    checked={backgroundRepeat === "repeat"}
                    onChange={(e) => setBackgroundRepeat(e.target.value)} />
                  <label htmlFor="repeat">Repeat</label>
                </div>
              </fieldset>
              <fieldset>
                <legend>Background size</legend>
                <input
                  type="range"
                  name="background-size"
                  min="0"
                  max="200"
                  value={backgroundSize}
                  onChange={(e) => setBackgroundSize(e.target.value)}
                />
                <span>{backgroundSize}</span>
              </fieldset>
              <fieldset>
                <legend>Background position</legend>
                <input
                  type="range"
                  name="background-position-x"
                  min="-200"
                  max="200"
                  value={backgroundPositionX}
                  onChange={(e) => setBackgroundPositionX(e.target.value)}
                />
                <input
                  type="range"
                  name="background-position-y"
                  min="-200"
                  max="200"
                  value={backgroundPositionY}
                  onChange={(e) => setBackgroundPositionY(e.target.value)}
                />
              </fieldset>
              <fieldset>
                <legend>Background animation:</legend>
                <div>
                  <input 
                    type="radio" 
                    id="move-up" 
                    name="background-animation" 
                    value="moveBackgroundX 1s linear infinite" 
                    onChange={(e) => setBackgroundAnimation(e.target.value)} />
                  <label htmlFor="move-up">Move Left</label>
                </div>
                <div>
                  <input 
                    type="radio" 
                    id="move-down" 
                    name="background-animation" 
                    value="moveBackgroundY 1s linear infinite" 
                    onChange={(e) => setBackgroundAnimation(e.target.value)} />
                  <label htmlFor="move-down">Move Down</label>
                </div>
              </fieldset>
            </div>
          </div>
          <div className="button host-buttons" onClick={handleUploadStyle}>Upload style</div>
          {uploading && "Loading.."}
        </div>
        <div className="saved-card-styles-wrapper">
          <br />
          <h4>Your card styles</h4>
          <div className="saved-cards">
            {cardStyles.map(item => (
              <div className="card display chosen" key={item._id} style={{
                backgroundImage: `url(${item.url})`,
                backgroundRepeat: item.backgroundRepeat,
                backgroundSize: item.backgroundSize + "%",
                backgroundPositionX: item.backgroundPosition[0] + "%",
                backgroundPositionY: item.backgroundPosition[1] + "%",
                animation: item.backgroundAnimation
              }}>
                <div onClick={(e) => handleDeleteStyle(item._id, e)} className="delete-card-style">x</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Modal closeHandler={handleCloseModal} isOpened={isModalOpened}>
        <div className="change-profile">
          <h3>Upload Image</h3>
          <input
            type="file"
            id="imageUpload2"
            accept="image/*"
            style={{display: "none"}}
            className="button gray"
            onChange={handleFileChange}
          />
          <label htmlFor="imageUpload2" className="button gray">
            Choose a file
          </label>
          <p>{selectedFile?.name}</p>
          <button className="button gray" onClick={handleUpload}>Upload</button>
          {uploading && "Uploading..."}
          {error}
        </div>
      </Modal>
    </div>  

  );
};

export default CardEditor;

